import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  UserPlus,
  RefreshCw,
  AlertCircle,
  Shield,
  Users,
  Search,
  Edit2,
  Trash2,
  Mail,
  Clock,
} from "lucide-react";
import { userApi } from "../services/api";
import UserFormModal from "../components/UserFormModal";
import UserDeleteModal from "../components/UserDeleteModal";
import { formatDate } from "../utils/dateFormatter";

const defaultMeta = {
  total: 0,
  page: 1,
  pageSize: 20,
  totalPages: 1,
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState(defaultMeta);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [toast, setToast] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isFirstSearch = useRef(true);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const dismissToastAfterDelay = useCallback(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => dismissToastAfterDelay(), [toast, dismissToastAfterDelay]);

  const getErrorMessage = useCallback((error) => {
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error instanceof Error) return error.message;
    return "Something went wrong. Please try again.";
  }, []);

  const fetchUsers = useCallback(
    async (query = "") => {
      try {
        setIsLoading(true);
        setFetchError(null);
        const params = query ? { search: query } : undefined;
        const response = await userApi.list(params);

        if (!response.success || !response.data) {
          throw new Error(response.error || "Unable to fetch users");
        }

        setUsers(response.data.users || []);
        setRoles(response.data.roles || []);
        setMeta(response.data.meta || defaultMeta);
      } catch (error) {
        console.error("Failed to fetch users", error);
        setFetchError(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [getErrorMessage],
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const handler = setTimeout(() => {
      fetchUsers(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput, fetchUsers]);

  const handleCreateUser = async (payload) => {
    try {
      setFormLoading(true);
      const response = await userApi.create(payload);

      if (!response.success) {
        throw new Error(response.error || "Failed to create user");
      }

      showToast("New administrator added successfully");
      setFormOpen(false);
      setEditingUser(null);
      await fetchUsers(searchInput);
    } catch (error) {
      console.error("Create user failed", error);
      showToast(getErrorMessage(error), "error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (payload) => {
    if (!editingUser) return;
    try {
      setFormLoading(true);
      const response = await userApi.update(editingUser.id, payload);

      if (!response.success) {
        throw new Error(response.error || "Failed to update user");
      }

      showToast("Administrator updated");
      setFormOpen(false);
      setEditingUser(null);
      await fetchUsers(searchInput);
    } catch (error) {
      console.error("Update user failed", error);
      showToast(getErrorMessage(error), "error");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      const response = await userApi.delete(deleteTarget.id);

      if (!response.success) {
        throw new Error(response.error || "Failed to delete user");
      }

      showToast("Administrator removed");
      setDeleteTarget(null);
      await fetchUsers(searchInput);
    } catch (error) {
      console.error("Delete user failed", error);
      showToast(getErrorMessage(error), "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = (payload) => {
    if (editingUser) {
      handleUpdateUser(payload);
    } else {
      handleCreateUser(payload);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    if (formLoading) return;
    setFormOpen(false);
    setEditingUser(null);
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
  };

  const superAdminCount = users.filter((user) => user.role?.name === "super-admin").length;

  // Date formatting now handled by utils/dateFormatter with IST timezone

  return (
    <div className="space-y-4">
      {toast && (
        <div
          className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${
            toast.type === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {toast.type === "error" ? <AlertCircle size={16} /> : <Shield size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {fetchError && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5" size={18} />
          <div>
            <p className="font-semibold text-red-900">Unable to load administrators</p>
            <p>{fetchError}</p>
            <button
              onClick={() => fetchUsers(searchInput)}
              className="mt-2 inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              <RefreshCw size={14} className="text-red-700" />
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Admin Users</h3>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative w-full sm:w-64">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fetchUsers(searchInput)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <UserPlus size={16} />
                Invite User
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs uppercase text-gray-500">Total Admins</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{meta.total}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs uppercase text-gray-500">Super Admins</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{superAdminCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs uppercase text-gray-500">Roles Available</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{roles.length}</p>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
            <RefreshCw size={28} className="animate-spin text-blue-600" />
            <p className="text-sm">Loading administrators...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
            <AlertCircle size={32} />
            <p className="text-sm">
              No administrators found. Invite your first teammate to get started.
            </p>
            <button
              type="button"
              onClick={handleOpenCreateModal}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <UserPlus size={16} />
              Add Admin
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-4">
                      <p className="font-medium text-gray-900">{user.name || "—"}</p>
                      <p className="text-xs text-gray-500">ID #{user.id}</p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail size={14} className="text-gray-400" />
                        <span>{user.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        <Shield size={12} />
                        {user.role?.name || "Unknown"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-600">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditClick(user)}
                          className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:border-blue-200 hover:text-blue-600"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(user)}
                          className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:border-red-200 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && users.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-gray-100 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
            <p>
              Showing <span className="font-semibold">{users.length}</span> of{" "}
              <span className="font-semibold">{meta.total}</span> admins
            </p>
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-full border border-gray-200 px-3 py-1">
                Page {meta.page} of {meta.totalPages}
              </span>
            </div>
          </div>
        )}
      </div>

      <UserFormModal
        isOpen={formOpen}
        mode={editingUser ? "edit" : "create"}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        roles={roles}
        isSubmitting={formLoading}
        initialData={editingUser}
        key={editingUser ? `edit-${editingUser.id}` : "create"}
      />

      <UserDeleteModal
        user={deleteTarget}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleDeleteUser}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default UserManagement;
